import { forwardRef, useState } from 'react'
import { Check, Eye, EyeOff, X } from 'lucide-react'
import { passwordIssues, PASSWORD_MIN_LENGTH } from '@concours/shared'
import { Input, type InputProps } from '../components/Field'

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** Affiche la liste des règles (création / changement de mot de passe). */
  showRules?: boolean
  /** Adresse électronique : vérifie que le mot de passe ne la contient pas. */
  email?: string
}

const RULES = [
  {
    key: 'length',
    label: `${PASSWORD_MIN_LENGTH} caractères minimum`,
    test: (issues: string[]) =>
      !issues.some((i) => i.startsWith('Au moins 10') || i.startsWith('Au plus')),
  },
  {
    key: 'letter',
    label: 'Une lettre',
    test: (issues: string[]) => !issues.includes('Au moins une lettre.'),
  },
  {
    key: 'digit',
    label: 'Un chiffre',
    test: (issues: string[]) => !issues.includes('Au moins un chiffre.'),
  },
  {
    key: 'id',
    label: 'Sans votre identifiant',
    test: (issues: string[]) => !issues.some((i) => i.includes('identifiant')),
  },
]

/** Champ mot de passe avec affichage/masquage et règles vérifiées en direct. */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ showRules = false, email, value, ...rest }, ref) {
    const [visible, setVisible] = useState(false)
    const current = typeof value === 'string' ? value : ''
    const issues = passwordIssues(current, email)

    return (
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <div style={{ position: 'relative' }}>
          <Input
            ref={ref}
            type={visible ? 'text' : 'password'}
            value={value}
            style={{ paddingInlineEnd: 44 }}
            {...rest}
          />
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-sm"
            style={{
              position: 'absolute',
              insetInlineEnd: 4,
              insetBlockEnd: rest.error || rest.hint ? 28 : 5,
            }}
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            aria-pressed={visible}
          >
            {visible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </button>
        </div>
        {showRules && (
          <ul className="pw-rules" aria-label="Règles du mot de passe">
            {RULES.map((rule) => {
              const ok = current.length > 0 && rule.test(issues)
              return (
                <li key={rule.key} data-ok={ok}>
                  {ok ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
                  <span>{rule.label}</span>
                  <span className="sr-only">{ok ? ' : respecté' : ' : non respecté'}</span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    )
  }
)
